import BehaviorConfig from '@/models/app-config/behavior/behavior'
import _ from 'lodash'

import { log } from '@/log'
import { DiscountAmount } from '@/models/discount-amount/discount-amount'
import { ScriptLoader } from '@/util/scriptLoader'
import { CartItem } from '@spa/store/modules/cart/types'
import { Contact } from '@spa/store/modules/contact/types'
import { Product } from '@spa/store/modules/orderHistory/types'
import {
  calculateDaysAfterCreated,
  getFullName as getUserFullName,
  getPhoneticFullName as getUserPhoneticFullName,
} from '@spa/store/modules/user/functions'
import { User } from '@spa/store/modules/user/types'

export function configPaidy(callback: (response: PaidyResponse) => Promise<void>) {
  const config = {
    api_key: process.env.PAYMENT_PAIDY_API_KEY,
    logo_url: 'http://www.paidy.com/images/logo.png',
    closed: callback,
  }

  return Paidy.configure(config) as PaidyHandler
}

export async function loadPaidyScript(): Promise<void> {
  const src = process.env.PAYMENT_PAIDY_SCRIPT || ''

  const scriptLoader = new ScriptLoader({
    src,
    global: 'Paidy',
  })

  await scriptLoader.load()
}

/**
 * Make payload compliant with Paidy format.
 */
export function makePaidyPayload(
  salesOrderId: string,
  user: User,
  contact: Contact,
  items: PaidyItem[],
  shipping: number
): PaidyPayload {
  const buyer = {
    email: user.email!,
    name1: getUserFullName(user, ' '),
    name2: getUserPhoneticFullName(user, ' '),
    phone: user.tel!,
    dob: user.birthday!,  // date of birth
  }

  const buyerData = {
    user_id: user.id,
    age: calculateDaysAfterCreated(user),
  }

  const order = {
    items,
    order_ref: salesOrderId,
    shipping,
  }

  const shippingAddress = {
    zip: contact.postal_code!,
    state: contact.region!,
    city: contact.locality!,
    line1: contact.street_address!,
  }

  return {
    amount: calculateAmount(items, shipping),
    currency: 'JPY',
    store_name: BehaviorConfig.forHost.groupLabel,
    buyer,
    buyer_data: buyerData,
    order,
    shipping_address: shippingAddress,
  }
}

// Discount price is applied only to an ordinary purchase, not to reorder.
interface ConvertOption {
  serviceFee?: number
}
export function convertCartItems(
  items: CartItem[],
  discountAmount: DiscountAmount,
  option: ConvertOption = {}
): PaidyItem[] {
  return [
    ...items.map(convertCartItem),
    ...discountAmount.totalDiscountAmount ? [makeDiscountItem(discountAmount.totalDiscountAmount)] : [],
    ...option.serviceFee ? [makeServiceFeeItem(option.serviceFee)] : [],
  ]
}

export function convertOrderHistoryProducts(
  items: Product[],
  discountAmount: DiscountAmount,
  option: ConvertOption = {}
): PaidyItem[] {
  return [
    ...items.map(convertOrderHistoryProduct),
    ...discountAmount.totalDiscountAmount ? [makeDiscountItem(discountAmount.totalDiscountAmount)] : [],
    ...option.serviceFee ? [makeServiceFeeItem(option.serviceFee)] : [],
  ]
}

export function convertCartItem(item: CartItem): PaidyItem {
  return {
    id: item.skuId,
    quantity: item.unitCount,
    title: item.name,
    unit_price: item.unitPrice,
  }
}

function makeDiscountItem(discountPrice: number): PaidyItem {
  return {
    id: 'discount',
    quantity: 1,
    title: 'クーポン割引',
    unit_price: -discountPrice,
  }
}
function makeServiceFeeItem(serviceFee: number): PaidyItem {
  return {
    id: 'service_fee',
    quantity: 1,
    title: 'サービス手数料',
    unit_price: serviceFee,
  }
}

export async function paidyCallbackFactory(
  response: PaidyResponse,
  action: (response: PaidyResponse) => Promise<void>
): Promise<void> {
  log.debug({
    paidy_callback: response,
  })

  await action(response)
}

export function convertOrderHistoryProduct(item: Product): PaidyItem {
  return {
    id: item.skuId,
    quantity: item.unitCount,
    title: item.itemName!,
    unit_price: item.unitPrice,
  }
}

/**
 * See https://paidy.com/docs/jp/paidycheckout.html
 */
export interface PaidyPayload {
  amount: number,
  currency: string,
  store_name: string,
  buyer: PaidyBuyer,
  buyer_data: PaidyBuyerData,
  order: PaidyOrder,
  shipping_address: PaidyShippingAddress,
  description?: string,
}

/**
 * See https://paidy.com/docs/jp/paidycheckout.html
 */
export interface PaidyBuyer {
  email: string,
  name1: string, // Full name(Kanji)
  name2: string, // Full name(Phonetic)
  phone: string,
  dob: string,
}

/**
 * See https://paidy.com/docs/jp/paidycheckout.html
 */
export interface PaidyBuyerData {
  user_id: string,
  age: number,
  order_count?: number, // This field cannot be retrieved from Engine at this point.
  ltv?: number, // This field cannot be retrieved from Engine at this point.
  last_order_amount?: number, // This field cannot be retrieved from Engine at this point.
  last_order_at?: number, // This field cannot be retrieved from Engine at this point.
}

export interface PaidyOrder {
  items: PaidyItem[],
  order_ref: string,
  shipping?: number,
  tax?: number,
}

/**
 * See https://paidy.com/docs/jp/paidycheckout.html
 */
export interface PaidyItem {
  id: string,
  quantity: number,
  title: string,
  unit_price: number,
  description?: string,
}

/**
 * See https://paidy.com/docs/jp/paidycheckout.html
 */
export interface PaidyShippingAddress {
  line1: string,
  line2?: string,
  city: string,
  state: string,
  zip: string,
}

/**
 * See https://paidy.com/docs/jp/paidycheckout.html
 */
export interface PaidyResponse {
  amount: number,
  currency: string,
  created_at: string,
  id: string,
  status: string,
}

function calculateAmount(items: PaidyItem[], shipping: number): number {
  const subtotal = _.sum(
    _.map(items, item => item.unit_price * item.quantity)
  )

  return subtotal + shipping
}
